import { ArrowDownLeft, ArrowUpRight } from "lucide-react";
import Loading from "./common/LoaderCommon";

type Props = {
  isLoading?: boolean;
  entries?: {
    referenceId: string;
    credit: number | null;
    debit: number | null;
    method: string;
  }[];
  totalCredit?: number | null;
  totalDebit?: number | null;
  receivable?: number | null;
  payable?: number | null;
  fmt?: (value: number | null) => string;
};

const safeFormatNumber = (val: number | null | undefined): string => {
  if (val === null || val === undefined || isNaN(val)) return "0";
  return Number(val).toLocaleString("en-IN", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
};

const defaultFmt = (val: number | null) =>
  val !== null && val !== undefined ? Number(val).toLocaleString("en-IN", { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : "—";

const AccountingListTable = (props: Props) => {
  const entries = props.entries ?? [];
  const formatFn = props.fmt ?? defaultFmt;

  return (
    <div className="flex-1 rounded-[20px] border border-[#E8EEF9] bg-white shadow-[0_8px_24px_rgba(26,94,249,0.06)] overflow-hidden">
      {/* Table header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-[#E8EEF9]">
        <div>
          <h3 className="text-base font-semibold text-[#21314A]">
            Today's Transactions
          </h3>
          <p className="text-xs text-[#8A9BB5] mt-0.5">
            {entries.length} entries recorded
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <ArrowDownLeft className="w-3.5 h-3.5 text-[#0E9F6E]" />
            <span className="text-xs font-medium text-[#0E9F6E]">
              Credit: OMR {safeFormatNumber(props.totalCredit)}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <ArrowUpRight className="w-3.5 h-3.5 text-[#D14343]" />
            <span className="text-xs font-medium text-[#D14343]">
              Debit: OMR {safeFormatNumber(props.totalDebit)}
            </span>
          </div>
          <div className="flex items-center gap-1.5 border-l border-[#E8EEF9] pl-4">
            <span className="text-xs font-medium text-[#647164]">
              Receivable: OMR {safeFormatNumber(props.receivable)}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium text-[#647164]">
              Payable: OMR {safeFormatNumber(props.payable)}
            </span>
          </div>
        </div>
      </div>

      {props.isLoading ? (
        <div className="flex min-h-[200px] items-center justify-center">
          <Loading />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F5F8FD] text-left text-xs font-semibold text-[#31425E] uppercase tracking-wide">
                <th className="py-3 px-6">Reference</th>
                <th className="py-3 px-6 text-right">Credit</th>
                <th className="py-3 px-6 text-right">Debit</th>
                <th className="py-3 px-6">Method</th>
              </tr>
            </thead>
            <tbody>
              {entries.length > 0 ? (
                entries.map((row, idx) => (
                  <tr
                    key={idx}
                    className="border-t border-[#EEF2FA] text-sm text-[#43536A] hover:bg-[#FAFCFF] transition-colors"
                  >
                    <td className="py-3.5 px-6 font-medium text-[#1A5EF9]">
                      {row.referenceId}
                    </td>
                    <td className="py-3.5 px-6 text-right font-medium text-[#0E9F6E]">
                      {row.credit ? formatFn(row.credit) : "—"}
                    </td>
                    <td className="py-3.5 px-6 text-right font-medium text-[#D14343]">
                      {row.debit ? formatFn(row.debit) : "—"}
                    </td>
                    <td className="py-3.5 px-6">
                      <span className="inline-flex rounded-full bg-[#F0F4FF] text-[#4B6EF5] text-xs font-semibold px-2.5 py-1">
                        {row.method}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={4}
                    className="py-16 text-center text-sm text-[#8D98A7]"
                  >
                    No transactions recorded today
                  </td>
                </tr>
              )}
            </tbody>
            {entries.length > 0 && (
              <tfoot>
                <tr className="bg-[#F0F4FF] border-t border-[#D8E4FB]">
                  <td className="py-3.5 px-6 font-bold text-[#21314A]">
                    Total
                  </td>
                  <td className="py-3.5 px-6 text-right font-bold text-[#0E9F6E]">
                    OMR {safeFormatNumber(props.totalCredit)}
                  </td>
                  <td className="py-3.5 px-6 text-right font-bold text-[#D14343]">
                    OMR {safeFormatNumber(props.totalDebit)}
                  </td>
                  <td className="py-3.5 px-6" />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
};

export default AccountingListTable;
